import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ScheduledReportsService } from '../../../shared/services/scheduled-reports.service';
import { ToastService } from '../../../shared/services/toast.service';
import { saveAs } from 'file-saver';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { ActivatedRoute } from '@angular/router';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
    selector: 'app-scheduled-reports',
    templateUrl: './scheduled-reports.component.html',
    styleUrls: ['./scheduled-reports.component.css'],
})
export class ScheduledReportsComponent implements OnInit {
    tab: number = 0;
    schedules: any[];
    exports: any[];

    directDownloadIdentifier: string;

    schedulesGrid: GridDataResult;
    exportsGrid: GridDataResult;

    loadingSchedules = true;
    loadingExports = true;

    scheduleOptions: any;

    pageSizeS = 10;
    skipS = 0;
    pageSizeE = 10;
    skipE = 0;

    constructor(
        private scheduledReportService: ScheduledReportsService,
        private dialogRef: MatDialog,
        private toastService: ToastService,
        private reusableFunctions: ReusableFunctions,
        private activatedRoute: ActivatedRoute
    ) {
        this.activatedRoute.queryParams.subscribe((res) => {
            if (res && res['download']) {
                this.directDownloadIdentifier = res['download'];
            }
        });
    }

    ngOnInit(): void {
        this.scheduleOptions = this.scheduledReportService.getOptions();
        this.load();
    }

    public handleTabChange(event: MatTabChangeEvent): void {
        this.tab = event.index;
    }

    load() {
        this.getSchedules();
        this.getExports();
    }

    public getSchedules() {
        this.loadingSchedules = true;
        this.scheduledReportService.getScheduledByUser().subscribe(
            (res) => {
                this.schedules = res;

                this.setGridViewSchedules();

                this.loadingSchedules = false;
            },
            (error) => {
                this.loadingSchedules = false;
            }
        );
    }

    public getExports() {
        this.loadingExports = true;
        this.scheduledReportService.getExports().subscribe(
            (res) => {
                this.exports = res;

                this.setGridViewExports();

                if (this.directDownloadIdentifier) {
                    let matchingExport = this.exports.find(
                        (x) => x.blobIdentifier == this.directDownloadIdentifier
                    );

                    if (matchingExport) {
                        this.download(matchingExport);
                        this.directDownloadIdentifier = null;
                    }
                }
                this.loadingExports = false;
            },
            (error) => {
                this.loadingExports = false;
            }
        );
    }

    getDayOfWeek(day) {
        let dayItem = this.scheduleOptions.day.find((x) => x.value == day);

        if (dayItem) {
            return dayItem.name;
        }

        return '';
    }

    getHour(row) {
        let hour = parseInt(row.hour) + row.utcOffset;

        if (hour < 0) {
            hour = 24 + hour;
        }

        let item = this.scheduleOptions.hour.find((x) => x.value == hour);
        if (item) {
            return item.name;
        }

        return '';
    }

    cancelConfirm(item) {
        let ref = this.dialogRef.open(ConfirmationDialogComponent, {
            data: { message: `You are about to delete a scheduled report for ${item.reportName}.` },
        });

        ref.afterClosed().subscribe((result) => {
            if (result) {
                this.deleteSchedule(item);
            }
        });
    }

    deleteSchedule(item) {
        this.scheduledReportService.deleteSchedule(item.userScheduledReportKey).subscribe(
            (res) => {
                this.toastService.success(
                    `Scheduled report for ${item.reportName} has been deleted`
                );
                this.load();
            },
            (error) => {
                this.toastService.warning(
                    `Something went wrong deleting scheduled report for ${item.reportName}`
                );
            }
        );
    }

    download(item) {
        item.loading = true;

        try {
            this.toastService.info(`${item.reportName} export is downloading`);

            this.scheduledReportService.download(item.blobIdentifier).subscribe((res) => {
                if (res) {
                    let reportName =
                        item.reportName +
                        '_' +
                        this.reusableFunctions.getCurrentDateTimeStamp(item.enterDt) +
                        '_.xlsx';
                    saveAs(res, reportName);

                    this.toastService.success(`${item.reportName} export has finished downloading`);
                } else {
                    throw 'Nothing';
                }

                item.loading = false;
            });
        } catch (e) {
            this.toastService.warning(`Something went wrong downloading ${item.reportName} export`);
        }
    }

    pageChangeSchedules({ skip, take }: PageChangeEvent): void {
        this.skipS = skip;
        this.pageSizeS = take;
        this.setGridViewSchedules();
    }

    setGridViewSchedules() {
        this.schedulesGrid = {
            data: this.schedules.slice(this.skipS, this.skipS + this.pageSizeS),
            total: this.schedules.length,
        };
    }

    pageChangeExports({ skip, take }: PageChangeEvent): void {
        this.skipE = skip;
        this.pageSizeE = take;
        this.setGridViewExports();
    }

    setGridViewExports() {
        this.exportsGrid = {
            data: this.exports.slice(this.skipE, this.skipE + this.pageSizeE),
            total: this.exports.length,
        };
    }
}
